import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Event } from "@/lib/supabase";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateString: string, useLocalTime: boolean = true): string {
  if (!useLocalTime) {
    const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const monthIndex = parseInt(month, 10) - 1;
      return `${monthNames[monthIndex]} ${parseInt(day, 10)}, ${year}`;
    }
  }
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
}

export function formatTime(timeString: string): string {
  // Handle if timeString is a full ISO date
  if (timeString.includes('T')) {
    const date = new Date(timeString);
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  }
  
  // Handle if timeString is just a time (HH:MM)
  try {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes);
    
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  } catch (e) {
    return timeString;
  }
}

// Get Django data from the template
export function getDjangoData() {
  const dataElement = document.getElementById('django-data');
  return dataElement ? JSON.parse(dataElement.textContent || '{}') : {};
};

export function getReactComponent() {
  const componentElement = document.getElementById('react-component');
  return componentElement ? JSON.parse(componentElement.textContent || '"App"') : 'App';
}

export function validateVideoUrl(url: string): { valid: boolean; platform?: string } {
  if (!url) return { valid: false };
  
  // YouTube validation
  if (url.includes('youtube.com/') || url.includes('youtu.be/')) {
    return { valid: true, platform: 'youtube' };
  }
  
  // Instagram validation (simple)
  if (url.includes('instagram.com/')) {
    return { valid: true, platform: 'instagram' };
  }
  
  // TikTok validation (simple)
  if (url.includes('tiktok.com/')) {
    return { valid: true, platform: 'tiktok' };
  }
  
  return { valid: false };
}

export function getVideoEmbedUrl(url: string, platform: string): string {
  if (platform === 'youtube') {
    // Extract YouTube video ID
    let videoId = '';
    if (url.includes('youtube.com/watch')) {
      videoId = new URL(url).searchParams.get('v') || '';
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0];
    }
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
  }
  
  // For other platforms, just return the original URL
  // In a production app, you would need more sophisticated embedding for Instagram and TikTok
  return url;
}

export function getYouTubeVideoId(url: string): string {
  if (!url) return '';
  
  try {
    if (url.includes('youtube.com/watch')) {
      return new URL(url).searchParams.get('v') || '';
    } else if (url.includes('youtu.be/')) {
      return url.split('youtu.be/')[1].split('?')[0];
    }
  } catch (error) {
    console.error('Error extracting YouTube video ID:', error);
  }
  
  return '';
}

export function getYouTubeThumbnail(videoId: string, quality: 'default' | 'medium' | 'high' | 'maxres' = 'high'): string {
  if (!videoId) return '';
  
  switch (quality) {
    case 'default':
      return `https://img.youtube.com/vi/${videoId}/default.jpg`;
    case 'medium':
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    case 'high':
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    case 'maxres':
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    default:
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }
}

export function getTimeOptions(): string[] {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const hourFormatted = hour.toString().padStart(2, '0');
      const minuteFormatted = minute.toString().padStart(2, '0');
      options.push(`${hourFormatted}:${minuteFormatted}`);
    }
  }
  return options;
}

export function isValidGeoLocation(lat: number, lng: number): boolean {
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

export function groupRidesByTimeframe(rides: any[]) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const nextWeek = new Date(today);
  nextWeek.setDate(today.getDate() + 7);
  
  const nextMonth = new Date(today);
  nextMonth.setMonth(today.getMonth() + 1);
  
  return {
    today: rides.filter(ride => {
      const rideDate = new Date(ride.meeting_date);
      return rideDate >= today && rideDate < tomorrow;
    }),
    thisWeek: rides.filter(ride => {
      const rideDate = new Date(ride.meeting_date);
      return rideDate >= today && rideDate < nextWeek;
    }),
    thisMonth: rides.filter(ride => {
      const rideDate = new Date(ride.meeting_date);
      return rideDate >= today && rideDate < nextMonth;
    }),
    future: rides.filter(ride => {
      const rideDate = new Date(ride.meeting_date);
      return rideDate >= today;
    }),
  };
}

export function groupEventsByTimeframe(events: Event[]) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const nextWeek = new Date(today);
  nextWeek.setDate(today.getDate() + 7);
  
  const nextMonth = new Date(today);
  nextMonth.setMonth(today.getMonth() + 1);
  
  return {
    today: events.filter(event => {
      const eventDate = new Date(event.meeting_datetime);
      return eventDate >= today && eventDate < tomorrow;
    }),
    thisWeek: events.filter(event => {
      const eventDate = new Date(event.meeting_datetime);
      return eventDate >= today && eventDate < nextWeek;
    }),
    thisMonth: events.filter(event => {
      const eventDate = new Date(event.meeting_datetime);
      return eventDate >= today && eventDate < nextMonth;
    }),
    future: events.filter(event => {
      const eventDate = new Date(event.meeting_datetime);
      return eventDate >= today;
    }),
  };
}

export function calculatePlatformFee(price: number, revenueSplit: number = 10): number {
  return (price * revenueSplit) / 100;
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
}
